import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Select,
  Slider,
  Stack,
} from "@mui/material";
import React from "react";
import { SortingStrategy } from "../sortingStrategies";
import { ParkingConfigControl } from "./ParkingConfigControl";
import {
  validateArrivalStagingDwell,
  validateDepartureStagingDwell,
  validateQuickTurnThreshold,
} from "../hooks/useParkingConfig";
import {
  MIN_ARRIVAL_STAGING_MINUTES,
  MAX_ARRIVAL_STAGING_MINUTES,
  MIN_DEPARTURE_STAGING_MINUTES,
  MAX_DEPARTURE_STAGING_MINUTES,
  MIN_QUICK_TURN_THRESHOLD_MINUTES,
  MAX_QUICK_TURN_THRESHOLD_MINUTES,
} from "../types";

/**
 * Formats minutes into a human-readable time string
 * @param minutes - Number of minutes to format
 * @returns Formatted string (e.g., "30 min", "1 hour", "1 hour 15 min")
 */
function formatTimeMinutes(minutes: number): string {
  if (minutes === 0) return "0 min";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) {
    return `${mins} min`;
  } else if (mins === 0) {
    return hours === 1 ? "1 hour" : `${hours} hours`;
  } else {
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} min`;
  }
}

export interface ParkingConfigFormProps {
  bufferMinutes: number;
  plannedMoveCap: number;
  allowTrapping: boolean;
  egressWeight: number;
  wasteWeight: number;
  structuralWeight: number;
  zonePriorityWeight: number;
  min_dwell_weight: number;
  sortingStrategy: SortingStrategy;
  arrivalStagingDwellMinutes: number;
  departureStagingDwellMinutes: number;
  quickTurnThresholdMinutes: number;
  onBufferMinutesChange: (value: number) => void;
  onPlannedMoveCapChange: (value: number) => void;
  onAllowTrappingChange: (value: boolean) => void;
  onEgressWeightChange: (value: number) => void;
  onWasteWeightChange: (value: number) => void;
  onStructuralWeightChange: (value: number) => void;
  onZonePriorityWeightChange: (value: number) => void;
  onMinDwellWeightChange: (value: number) => void;
  onSortingStrategyChange: (value: SortingStrategy) => void;
  onArrivalStagingDwellMinutesChange: (value: number) => void;
  onDepartureStagingDwellMinutesChange: (value: number) => void;
  onQuickTurnThresholdMinutesChange: (value: number) => void;
  size?: "small" | "medium";
  layout?: "row" | "column";
  disabledFields?: string[];
  disabledSortingOptions?: SortingStrategy[];
}

/**
 * Reusable parking configuration form
 * Used by both AutoPark dialog and TemporalTest dev page
 */
export const ParkingConfigForm: React.FC<ParkingConfigFormProps> = ({
  bufferMinutes,
  plannedMoveCap,
  allowTrapping,
  egressWeight,
  wasteWeight,
  structuralWeight,
  zonePriorityWeight,
  min_dwell_weight,
  sortingStrategy,
  arrivalStagingDwellMinutes,
  departureStagingDwellMinutes,
  quickTurnThresholdMinutes,
  onBufferMinutesChange,
  onPlannedMoveCapChange,
  onAllowTrappingChange,
  onEgressWeightChange,
  onWasteWeightChange,
  onStructuralWeightChange,
  onZonePriorityWeightChange,
  onMinDwellWeightChange,
  onSortingStrategyChange,
  onArrivalStagingDwellMinutesChange,
  onDepartureStagingDwellMinutesChange,
  onQuickTurnThresholdMinutesChange,
  size = "small",
  layout = "row",
  disabledFields = [],
  disabledSortingOptions = [],
}) => {
  // Validate staging configuration values
  const arrivalValidation = validateArrivalStagingDwell(arrivalStagingDwellMinutes);
  const departureValidation = validateDepartureStagingDwell(departureStagingDwellMinutes);
  const thresholdValidation = validateQuickTurnThreshold(quickTurnThresholdMinutes);

  return (
    <Stack
      direction={layout}
      spacing={layout === "column" ? 1.5 : 1}
      alignItems={layout === "row" ? "flex-start" : "stretch"}
      flexWrap="wrap"
      sx={layout === "row" ? { p: 1, borderRadius: 1 } : undefined}
    >
      <ParkingConfigControl
        title="Buffer"
        helpText="Extra time (minutes) added before arrival and after departure to prevent tight spacing conflicts. Example: Aircraft departs at 10:00. With 15min buffer, spot becomes available at 10:15."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={bufferMinutes}
            onChange={(e) => onBufferMinutesChange(Number(e.target.value))}
            disabled={disabledFields.includes("bufferMinutes")}
          >
            <MenuItem value={0}>No Buffer (0)</MenuItem>
            <MenuItem value={10}>10 minutes</MenuItem>
            <MenuItem value={15}>Standard (15)</MenuItem>
            <MenuItem value={20}>High (20)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Planned Moves"
        helpText="Maximum number of times an aircraft can be relocated during its dwell. 0 = no moves (aircraft stays in one spot). Example: With 1 move allowed, aircraft could park at Spot A (8:00-12:00) then move to Spot B (12:00-16:00)."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={plannedMoveCap}
            onChange={(e) => onPlannedMoveCapChange(Number(e.target.value))}
            disabled={disabledFields.includes("plannedMoveCap")}
          >
            <MenuItem value={0}>No Splits (0)</MenuItem>
            <MenuItem value={1}>Allow 1 Move</MenuItem>
            <MenuItem value={2}>Allow 2 Moves</MenuItem>
            <MenuItem value={3}>Allow 3 Moves</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Allow Trapping"
        helpText="When checked, allows placing aircraft in spots that block others' departure paths. When unchecked, only places aircraft in spots that don't trap others (may leave aircraft unplaced)."
        layout={layout}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allowTrapping}
              onChange={(e) => onAllowTrappingChange(e.target.checked)}
              size={size}
            />
          }
          label="Allow Trapping"
        />
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Egress Weight"
        helpText="Controls how strongly the algorithm avoids placements that trap other aircraft. Low (1x): minimal penalty. Medium (2x): moderate penalty. High (5x): strong penalty."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={egressWeight}
            onChange={(e) => onEgressWeightChange(Number(e.target.value))}
          >
            <MenuItem value={1.0}>Low (1x)</MenuItem>
            <MenuItem value={2.0}>Medium (2x)</MenuItem>
            <MenuItem value={5.0}>High (5x)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Waste Weight"
        helpText="Controls how strongly the algorithm avoids placing small aircraft in oversized spots. Helps reserve premium large spots for large aircraft that actually need them."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={wasteWeight}
            onChange={(e) => onWasteWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Disabled (0x)</MenuItem>
            <MenuItem value={0.5}>Low (0.5x)</MenuItem>
            <MenuItem value={1.0}>Medium (1x)</MenuItem>
            <MenuItem value={2.0}>High (2x)</MenuItem>
            <MenuItem value={3.0}>Very High (3x)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Structural Weight"
        helpText="Controls penalty for spots with complex blocking topology. Encourages using 'clean' spots first, saving complex spots as last resort. Low (0.05x): minimal. Standard (0.1x): tiebreaker. High (0.5x): strong preference."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={structuralWeight}
            onChange={(e) => onStructuralWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Disabled (0x)</MenuItem>
            <MenuItem value={0.05}>Low (0.05x)</MenuItem>
            <MenuItem value={0.1}>Standard (0.1x)</MenuItem>
            <MenuItem value={0.2}>Medium (0.2x)</MenuItem>
            <MenuItem value={0.5}>High (0.5x)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Zone Priority Weight"
        helpText="Controls preference for high-priority zones. Disabled (0x): ignore zones. Low (0.5x): minor preference. Standard (1x): balanced. High (2x): strong preference. Very High (3x): maximum preference."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={zonePriorityWeight}
            onChange={(e) => onZonePriorityWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Disabled (0x)</MenuItem>
            <MenuItem value={0.5}>Low (0.5x)</MenuItem>
            <MenuItem value={1.0}>Standard (1x)</MenuItem>
            <MenuItem value={2.0}>High (2x)</MenuItem>
            <MenuItem value={3.0}>Very High (3x)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Min Dwell Weight"
        helpText="Controls preference for spots with min dwell requirements. Attracts long-stay aircraft to remote/overflow spots and repels short-stay aircraft. Disabled (0x): ignore min dwell. Low (0.5x): minor preference. Standard (1x): balanced. High (2x): strong preference. Very High (3x): maximum preference."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={min_dwell_weight}
            onChange={(e) => onMinDwellWeightChange(Number(e.target.value))}
          >
            <MenuItem value={0}>Disabled (0x)</MenuItem>
            <MenuItem value={0.5}>Low (0.5x)</MenuItem>
            <MenuItem value={1.0}>Standard (1x)</MenuItem>
            <MenuItem value={2.0}>High (2x)</MenuItem>
            <MenuItem value={3.0}>Very High (3x)</MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Arrival Staging Time"
        helpText={`Duration for arrival servicing operations: unloading, customs, refueling, cleaning. Set to 0 to disable arrival staging. Requires staging zones configured on ramp. Valid range: 0-${MAX_ARRIVAL_STAGING_MINUTES} minutes.`}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Slider
            value={arrivalStagingDwellMinutes}
            onChange={(_, value) => onArrivalStagingDwellMinutesChange(value as number)}
            disabled={disabledFields.includes("arrivalStagingDwellMinutes")}
            min={0}
            max={90}
            step={15}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTimeMinutes(value)}
          />
          <FormHelperText sx={{ mt: 1 }}>
            {arrivalStagingDwellMinutes === 0 ? "0 min (disabled)" : formatTimeMinutes(arrivalStagingDwellMinutes)}
            {!arrivalValidation.isValid && (
              <span style={{ marginLeft: '8px' }}>
                {arrivalValidation.message}
              </span>
            )}
          </FormHelperText>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Departure Staging Time"
        helpText={`Duration for departure preparation: boarding, final checks, paperwork. Set to 0 to disable departure staging. Requires staging zones configured on ramp. Valid range: 0-${MAX_DEPARTURE_STAGING_MINUTES} minutes.`}
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Slider
            value={departureStagingDwellMinutes}
            onChange={(_, value) => onDepartureStagingDwellMinutesChange(value as number)}
            disabled={disabledFields.includes("departureStagingDwellMinutes")}
            min={0}
            max={90}
            step={15}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => formatTimeMinutes(value)}
          />
          <FormHelperText sx={{ mt: 1 }}>
            {departureStagingDwellMinutes === 0 ? "0 min (disabled)" : formatTimeMinutes(departureStagingDwellMinutes)}
            {!departureValidation.isValid && (
              <span style={{ marginLeft: '8px' }}>
                {departureValidation.message}
              </span>
            )}
          </FormHelperText>
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Quick Turn Threshold"
        helpText={`Minimum total dwell time to use staging workflow. Aircraft with shorter stays bypass staging and go directly to permanent spots. Valid range: ${MIN_QUICK_TURN_THRESHOLD_MINUTES}-${MAX_QUICK_TURN_THRESHOLD_MINUTES} minutes.`}
        layout={layout}
      >
        <FormControl fullWidth size={size} error={!thresholdValidation.isValid}>
          <Select
            value={quickTurnThresholdMinutes}
            onChange={(e) => onQuickTurnThresholdMinutesChange(Number(e.target.value))}
            disabled={
              (arrivalStagingDwellMinutes === 0 && departureStagingDwellMinutes === 0) ||
              disabledFields.includes("quickTurnThresholdMinutes")
            }
          >
            <MenuItem value={60}>1 hour</MenuItem>
            <MenuItem value={90}>1.5 hours</MenuItem>
            <MenuItem value={120}>Standard (2 hours)</MenuItem>
            <MenuItem value={180}>3 hours</MenuItem>
            <MenuItem value={240}>4 hours</MenuItem>
          </Select>
          {!thresholdValidation.isValid && (
            <FormHelperText>{thresholdValidation.message}</FormHelperText>
          )}
        </FormControl>
      </ParkingConfigControl>

      <ParkingConfigControl
        title="Sorting Strategy"
        helpText="Controls the order in which aircraft are processed. Size-Constrained: places aircraft with fewer spot options first. Arrival: processes by arrival time. Hybrid: combines size-constrained + longest-dwell."
        layout={layout}
      >
        <FormControl fullWidth size={size}>
          <Select
            value={sortingStrategy}
            onChange={(e) =>
              onSortingStrategyChange(e.target.value as SortingStrategy)
            }
          >
            <MenuItem
              value="arrival"
              disabled={disabledSortingOptions.includes("arrival")}
            >
              Arrival Order
            </MenuItem>
            <MenuItem
              value="size-constrained"
              disabled={disabledSortingOptions.includes("size-constrained")}
            >
              Size-Constrained First
            </MenuItem>
            <MenuItem
              value="longest-dwell"
              disabled={disabledSortingOptions.includes("longest-dwell")}
            >
              Longest Dwell First
            </MenuItem>
            <MenuItem
              value="latest-departure"
              disabled={disabledSortingOptions.includes("latest-departure")}
            >
              Latest Departure First
            </MenuItem>
            <MenuItem
              value="hybrid"
              disabled={disabledSortingOptions.includes("hybrid")}
            >
              Hybrid (Size + Dwell)
            </MenuItem>
            <MenuItem
              value="largest-first"
              disabled={disabledSortingOptions.includes("largest-first")}
            >
              Largest First
            </MenuItem>
          </Select>
        </FormControl>
      </ParkingConfigControl>
    </Stack>
  );
};
